/**
 * Thin Plausible Analytics wrapper.
 *
 * Controlled by two env vars (both optional — omitting VITE_PLAUSIBLE_DOMAIN
 * disables analytics entirely so local dev stays clean):
 *   VITE_PLAUSIBLE_DOMAIN       e.g. "shop.globalcontrol.group"
 *   VITE_PLAUSIBLE_SCRIPT_URL   default: Plausible Cloud tagged-events script
 *
 * The script is loaded/unloaded at runtime based on the user's analytics
 * consent decision — see useAnalyticsConsent() in hooks/useAnalytics.ts.
 */

const DOMAIN: string = import.meta.env.VITE_PLAUSIBLE_DOMAIN ?? ''
const SCRIPT_SRC: string =
  import.meta.env.VITE_PLAUSIBLE_SCRIPT_URL ??
  'https://plausible.io/js/script.tagged-events.js'
const SCRIPT_ID = 'gcg-plausible'

type EventProps = Record<string, string | number | boolean>
type PlausibleFn = (event: string, opts?: { props?: EventProps; callback?: () => void }) => void

declare global {
  interface Window {
    plausible?: PlausibleFn
  }
}

export function loadAnalyticsScript(): void {
  if (!DOMAIN || document.getElementById(SCRIPT_ID)) return
  const s = document.createElement('script')
  s.id = SCRIPT_ID
  s.defer = true
  s.src = SCRIPT_SRC
  s.dataset['domain'] = DOMAIN
  document.head.appendChild(s)
}

export function unloadAnalyticsScript(): void {
  document.getElementById(SCRIPT_ID)?.remove()
  delete window.plausible
}

export function trackEvent(name: string, props?: EventProps): void {
  if (!DOMAIN || typeof window.plausible !== 'function') return
  window.plausible(name, props ? { props } : undefined)
}
